// Engagement_Dashboard.service.ts
import {
  ApiContext,
  ApprovalStatusRow,
  DashboardFiltersPayload,
  OfficeStatusRow,
  PartnerStatusRow,
  SignatureStatusRow,
  PartnerReturnTypeRow,
  OfficeForecastAmountRow,
  ReturnTypeSummaryRow,
  LineOfServiceForecastAmountRow,
} from "./EngagementDashboard.utils";

export type DashboardDataBundle = {
  approvalStatus: ApprovalStatusRow[];
  signatureStatus: SignatureStatusRow[];
  office: OfficeStatusRow[];
  primaryPartner: PartnerStatusRow[];
  primaryPartnerReturnType: PartnerReturnTypeRow[];
  officeForecastAmount: OfficeForecastAmountRow[];
  returnTypeSummary: ReturnTypeSummaryRow[];
  lineOfServiceForecastAmount: LineOfServiceForecastAmountRow[];
};

const withAuthHeaders = (): HeadersInit => {
  // IMPORTANT:
  // Your backend reads headers.get("Authorization") and validates token.
  // If validate_token expects raw token (not "Bearer <token>"), remove "Bearer ".
  // If it's already working for other calls with Bearer, keep it.
  const token = localStorage.getItem("token") || "";
  return {
    "Content-Type": "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };
};

const handleJson = async <T>(response: Response): Promise<ApiContext<T>> => {
  if (response.status === 401) {
    alert("Session ended, please log in again");
    return { success: false, message: "Unauthorized", data: undefined };
  }

  let json: any = null;
  try {
    json = await response.json();
  } catch {
    // If server returns non-JSON, we still want a useful error below
  }

  if (!response.ok) {
    const msg = json?.message || json?.error || response.statusText || "Request failed.";
    throw new Error(msg);
  }

  return (json || {}) as ApiContext<T>;
};

/**
 * Sanitize payload:
 * - Must be an object (never a list)
 * - Remove keys with empty arrays
 * - Remove "All"
 * - Remove falsy values
 *
 * Backend expects either:
 *  - omit the key
 *  - or send list of strings
 */
const normalizeFiltersPayload = (
  payload: DashboardFiltersPayload | null | undefined
): Record<string, string | number> => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return {};
  }

  const cleanAndStringify = (arr?: string[]) => {
    if (!Array.isArray(arr)) return undefined;

    const cleaned = arr
      .map((s) => (typeof s === "string" ? s.trim() : ""))
      .filter(Boolean)
      .filter((s) => s.toLowerCase() !== "all");

    return cleaned.length ? JSON.stringify(cleaned) : undefined;
  };

  const normalized: Record<string, string | number> = {};

  const bu = cleanAndStringify(payload.BusinessUnit);
  const office = cleanAndStringify(payload.Office);
  const partner = cleanAndStringify(payload.PrimaryPartner);
  const fy = cleanAndStringify(payload.FiscalYear);

  if (bu) normalized.BusinessUnit = bu;
  if (office) normalized.Office = office;
  if (partner) normalized.PrimaryPartner = partner;
  if (fy) normalized.FiscalYear = fy;

  return normalized;
};


const post = async <T>(
  url: string,
  payload?: DashboardFiltersPayload | null
): Promise<ApiContext<T>> => {
  const safePayload = normalizeFiltersPayload(payload);

  const response = await fetch(url, {
    method: "POST",
    headers: withAuthHeaders(),
    // MUST be an object JSON string, never []
    body: JSON.stringify(safePayload),
  });

  return handleJson<T>(response);
};

/**
 * API endpoints
 */
const endpoints = {
  dashboardAll: "/mdm/engagement-letter/dashboard/el_dashboard_all/",
};

/**
 * fetchDashboardData
 * - Backend does all filtering
 */
export const fetchDashboardData = async (
  baseUri: string,
  filtersPayload?: DashboardFiltersPayload | null
): Promise<DashboardDataBundle> => {
  const base = (baseUri || "").replace(/\/$/, "");

  const res = await post<DashboardDataBundle>(
    `${base}${endpoints.dashboardAll}`,
    filtersPayload
  );

  if (res.errors && Object.keys(res.errors).length > 0) {
    console.warn("EL Dashboard section errors:", res.errors);
  }

  return {
    approvalStatus: res.data?.approvalStatus || [],
    signatureStatus: res.data?.signatureStatus || [],
    office: res.data?.office || [],
    primaryPartner: res.data?.primaryPartner || [],
    primaryPartnerReturnType: res.data?.primaryPartnerReturnType || [],
    officeForecastAmount: res.data?.officeForecastAmount || [],
    returnTypeSummary: res.data?.returnTypeSummary || [],
    lineOfServiceForecastAmount: res.data?.lineOfServiceForecastAmount || [],
  };
};
